import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ApiError, rewritePassage } from "../api";
import SheetHeader from "../components/SheetHeader";
import { SendIcon } from "../components/icons";
import { useT } from "../i18n";
import type { Placement } from "./placement";

interface Turn {
  instruction: string;
  text: string;
}

/**
 * RewriteSheet is a conversation with the model about one passage (ADR-0025).
 *
 * Each instruction produces a new version of the latest one, so the author
 * steers — "shorter", then "but keep the dialogue" — rather than starting over.
 * Any version can be placed in the editor; nothing is saved until the chapter
 * is. The conversation is this sheet's alone: closing it drops the versions
 * nobody placed, which is what makes a rejected draft cost nothing but tokens.
 *
 * Sending is an explicit tap only. Every turn is a charge to the book's library.
 */
export default function RewriteSheet({
  chapterID,
  passage,
  before,
  after,
  onPlace,
  onClose,
}: {
  chapterID: string;
  passage: string;
  before: string;
  after: string;
  onPlace: (text: string, how: Placement) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const log = useRef<HTMLDivElement>(null);

  const rewrite = useMutation({
    mutationFn: (v: { instruction: string; turns: Turn[] }) =>
      rewritePassage(chapterID, {
        passage,
        current: v.turns.at(-1)?.text,
        before,
        after,
        earlier: v.turns.map((turn) => turn.instruction),
        instruction: v.instruction,
      }),
    onSuccess: (result, v) =>
      setTurns((all) => [...all, { instruction: v.instruction, text: result.text }]),
    onError: (err, v) => {
      setProblem(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : t.edit.failed,
      );
      // Back where it was typed, so trying again is one tap.
      setDraft((current) => current || v.instruction);
    },
  });

  const send = () => {
    const instruction = draft.trim();
    if (!instruction || rewrite.isPending) return;
    setProblem(null);
    setDraft("");
    rewrite.mutate({ instruction, turns });
  };

  // Newest at the bottom: follow the conversation as it grows.
  useEffect(() => {
    const el = log.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns.length, rewrite.isPending]);

  return (
    <div
      role="dialog"
      aria-label={t.edit.rewriteTitle}
      data-testid="rewrite-sheet"
      /* Above the editor's bar (z-20), and a fixed height so a long version
         does not move the input under the thumb. */
      className="fixed inset-x-0 bottom-0 z-40 flex h-[85dvh] flex-col rounded-t-card border-t border-line bg-surface shadow-[0_-8px_24px_-12px_rgb(0_0_0/0.25)]"
    >
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col">
        <SheetHeader title={t.edit.rewriteTitle} onClose={onClose} closeTestID="rewrite-close" />

        <div
          ref={log}
          data-testid="rewrite-log"
          className="flex-1 space-y-5 overflow-y-auto px-4 py-4"
        >
          {turns.length === 0 && !rewrite.isPending && (
            <p className="text-sm text-muted" data-testid="rewrite-empty">
              {t.edit.empty}
            </p>
          )}
          {turns.map((turn, i) => (
            <div key={i} className="space-y-2">
              <Instruction text={turn.instruction} />
              <div
                data-testid="rewrite-version"
                className="rounded-card border border-line bg-ground p-3"
              >
                <p className="mb-1 text-xs text-muted">{t.edit.version(i + 1)}</p>
                <p dir="auto" className="text-[0.95rem] leading-8 whitespace-pre-wrap">
                  {turn.text}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="rewrite-replace"
                    onClick={() => onPlace(turn.text, "replace")}
                    className="rounded-control bg-sage px-3 py-1.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep"
                  >
                    {t.edit.replace}
                  </button>
                  <button
                    type="button"
                    data-testid="rewrite-insert"
                    onClick={() => onPlace(turn.text, "after")}
                    className="rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk"
                  >
                    {t.edit.insertAfter}
                  </button>
                </div>
              </div>
            </div>
          ))}
          {rewrite.isPending && (
            <div className="space-y-2">
              <Instruction text={rewrite.variables.instruction} />
              <p className="text-sm text-muted" data-testid="rewrite-pending" aria-live="polite">
                {t.edit.writing}
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-line px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <div className="mb-2 rounded-control border-s-2 border-sage bg-sunk px-2.5 py-1.5">
            <p className="text-[0.68rem] text-muted">{t.edit.selected}</p>
            {/* dir="auto" on the authored text and nothing inside it. */}
            <p
              dir="auto"
              data-testid="rewrite-passage"
              className="line-clamp-3 text-xs leading-5 text-ink-soft"
            >
              {passage}
            </p>
          </div>

          {problem && (
            <p
              role="alert"
              data-testid="rewrite-error"
              className="mb-2 text-sm text-red-600 dark:text-red-400"
            >
              {problem}
            </p>
          )}

          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <label className="min-w-0 flex-1">
              <span className="sr-only">{t.edit.yourInstruction}</span>
              <textarea
                dir="auto"
                autoFocus
                rows={1}
                value={draft}
                maxLength={1000}
                enterKeyHint="send"
                data-testid="rewrite-input"
                placeholder={turns.length ? t.edit.placeholderFollowUp : t.edit.placeholder}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  // Enter sends, Shift+Enter breaks the line; not mid-composition.
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    send();
                  }
                }}
                /* text-base: iOS zooms into any input under 16px on focus. */
                className="block max-h-32 w-full resize-none rounded-control border border-line bg-surface px-3 py-2 text-base [field-sizing:content]"
              />
            </label>
            <button
              type="submit"
              aria-label={t.edit.send}
              data-testid="rewrite-submit"
              disabled={draft.trim() === "" || rewrite.isPending}
              className="grid size-10 shrink-0 place-items-center rounded-full bg-sage text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
            >
              <SendIcon />
            </button>
          </form>
          <p className="mt-1.5 text-[0.7rem] text-muted">{t.edit.notSaved}</p>
        </div>
      </div>
    </div>
  );
}

function Instruction({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <p
        dir="auto"
        data-testid="rewrite-instruction"
        className="max-w-[85%] rounded-card rounded-ee-md bg-sage-soft px-3 py-2 text-[0.95rem] leading-7 whitespace-pre-wrap"
      >
        {text}
      </p>
    </div>
  );
}
